import { useEffect, useRef, useState } from "react";
import useWebSocket from "./useWebSocket";

type PriceUpdate = {
  token: number;
  price: number;
  ohlcClose?: number;
  change?: number;
};

export function useLivePrices() {
  const [prices, setPrices] = useState<Record<number, { price: number; ohlcClose?: number; change?: number }>>({});
  const { registerHandler, unregisterHandler, sendSubscribe, sendUnsubscribe, connected } =
    useWebSocket();

  const latestTokensRef = useRef<number[]>([]);
  const pendingUpdatesRef = useRef<Record<number, { price: number; ohlcClose?: number; change?: number }>>({});
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const getLivePrice = (newTokens: number[]) => {
    if (latestTokensRef.current.length > 0) {
      sendUnsubscribe(latestTokensRef.current);
    }
    sendSubscribe(newTokens);
    latestTokensRef.current = newTokens;
  };

  useEffect(() => {
    const handlerId = "livePrice_" + Date.now();

    registerHandler("STATUS", handlerId, (data: any) => {
      console.log("📡 WebSocket Status:", data);
    });

    registerHandler("PRICE_UPDATE", handlerId, (data: PriceUpdate) => {
      // Buffer the update in ref instead of setting state immediately
      pendingUpdatesRef.current[data.token] = { 
        price: data.price, 
        ohlcClose: data.ohlcClose, 
        change: data.change 
      };
    });

    // Commit pending updates to state every 1 second
    updateIntervalRef.current = setInterval(() => {
      if (Object.keys(pendingUpdatesRef.current).length > 0) {
        setPrices((prev) => ({
          ...prev,
          ...pendingUpdatesRef.current
        }));
        pendingUpdatesRef.current = {}; // Clear buffer
      }
    }, 1000);

    return () => {
      unregisterHandler("PRICE_UPDATE", handlerId);
      sendUnsubscribe(latestTokensRef.current);
      if (updateIntervalRef.current) clearInterval(updateIntervalRef.current);
    };
  }, []);

  return {
    prices,
    getLivePrice,
    connected,
  };
}
